<template>
  <v-card class="ma-0 pa-2 card" color="background" variant="elevated" :elevation="isOverlay ? 0 : 4" density="compact">
    <v-form class="ma-0 pa-0 card-content" @submit.prevent density="compact">
      <v-card-item v-if="!show" class="model-summary" @click="show = true">
        <template #prepend>
          <div class="mr-3 model-summary-avatar">
            <img
              v-if="showStandaloneIcon && iconMode !== 'mono'"
              :src="(icon as ProviderIcon).src"
              :alt="`${providerKey || 'model'} icon`"
              class="model-summary-avatar-image"
              @error="onIconError"
            >
            <span
              v-else-if="showStandaloneIcon"
              class="model-summary-avatar-mask"
              :style="iconMaskStyle"
              role="img"
              :aria-label="`${providerKey || 'model'} icon`"
            ></span>
            <span v-else class="model-summary-provider" :style="{ backgroundColor: brandColor }">{{ providerInitials }}</span>
          </div>
        </template>
        <v-card-title class="d-flex align-center">
          <span class="text-truncate">{{ name }}</span>
          <v-chip v-if="programmaticToolCalling" size="x-small" color="info" class="ml-2">Tools</v-chip>
          <v-chip v-if="isSystemModel" size="x-small" color="warning" class="ml-2">System</v-chip>
          <v-chip v-if="configuredModelRetired" size="x-small" color="warning" variant="tonal" class="ml-2">
            retired model
          </v-chip>
        </v-card-title>
        <v-card-subtitle class="text-truncate">
          {{ providerKey || 'No provider' }} · {{ modelName || 'No API model selected' }}
        </v-card-subtitle>
      </v-card-item>
      <v-card-text v-if="!show" class="pt-0 pb-0 model-summary-pricing">
        <div class="d-flex align-center">
          <div class="flex-grow-1 min-width-0">
            <div v-if="pricingLoading" class="text-caption text-medium-emphasis">Loading pricing…</div>
            <div v-else-if="pricingRecord">
              <div class="d-flex flex-wrap align-center">
                <v-chip size="small" variant="tonal" class="mr-2">
                  In {{ inputPricingLabel }}
                </v-chip>
                <v-chip size="small" variant="tonal" class="mr-2">
                  Out {{ outputPricingLabel }}
                </v-chip>
                <v-chip v-if="pricingTierLabel" size="small" variant="outlined" class="mr-2">
                  {{ pricingTierLabel }}
                </v-chip>
              </div>
              <div class="text-caption text-medium-emphasis">{{ pricingRecord.displayName }}</div>
            </div>
            <div v-else class="text-caption text-medium-emphasis">Pricing unavailable</div>
          </div>
          <v-btn v-if="!isOverlay" color="primary" icon="mdi-chevron-down" size="large" variant="plain"
            class="model-summary-expand" @click.stop="show = true" />
        </div>
      </v-card-text>
      <v-expand-transition>
        <v-list class="ma-0 pa-0 card-content" v-show="show" density="compact">
          <!-- Provider Selection -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Select the configured provider for this model.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="providerKey" @update:modelValue="providerChanged" :items="providerKeys" label="Provider"
              class="pt-2" variant="outlined" density="compact" :readonly="isSystemModel"
              :rules="[() => !!providerKey || 'Provider is required']"></v-select>
          </v-list-item>

          <!-- Provider Model Selection -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Select the specific model from the chosen provider's available models. Use the sync button to refresh the list.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-autocomplete v-model="modelName" @blur="checkHash" @update:modelValue="modelSelected" :items="models"
              item-title="id" item-value="id" label="Provider Models" class="pt-2" variant="outlined" density="compact"
              :disabled="isSystemModel || !providerKey" :loading="modelsLoading" :readonly="modelsLoading"
              :rules="[() => !!modelName || 'Provider model is required']">
              <template v-slot:append>
                <Button color="primary" buttonClass="ml-n2" :click="syncModels" :disabled="syncDisabled" icon="mdi-sync"
                  size="x-large" tooltip="Sync Models" />
              </template>
            </v-autocomplete>
          </v-list-item>

          <!--
            Retired model warning: the selected id is absent from the provider's discovered model
            list, so generation with it fails at the provider. Stored configs are never rewritten —
            the user re-selects. Suppressed entirely when the discovered list is empty/not loaded.
          -->
          <v-list-item v-if="selectedModelRetired && !isSystemModel" class="ma-0 pa-0" density="compact">
            <v-alert type="warning" density="compact" variant="tonal" class="mb-2">
              "{{ modelName }}" is no longer offered by {{ providerKey }}. Generation with this model will fail —
              select a current model above, then save.
            </v-alert>
          </v-list-item>

          <!-- Model Name -->
          <v-list-item v-if="showAdvanced" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'"
            density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="The Model Name defined here must be unique among your models and will appear in the models list for each agent once saved.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-text-field v-model="name" @blur="checkHash" label="Model Name" class="pt-2" :rules="modelNameRules"
              variant="outlined" density="compact" :readonly="isSystemModel || !isCopy"
              :clearable="!isSystemModel && isCopy">
              <template v-slot:append-inner>
                <v-tooltip text="Autofill from selected API model" v-if="name == null || name == ''">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" color="primary" icon="mdi-lightning-bolt-outline" @click="autofillName" />
                  </template>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-list-item>

          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" v-if="showAdvanced"
            density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="A brief description of the model. You can click the bolt at the end of the field to autofill the description.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-text-field v-model="description" :loading="loading" :disabled="loading || isSystemModel" class="pt-2"
              @blur="checkHash" label="Model Description" variant="outlined" density="compact">
              <template v-slot:append-inner>
                <v-tooltip text="Autofill (Not yet implemented)">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" color="primary" icon="mdi-lightning-bolt-outline"
                      @click="autofillDescription" />
                  </template>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-list-item>
          <v-list-item v-if="showAdvanced" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Allow this model config to request provider-native programmatic tool calling when supported. Anthropic Sonnet/Opus 4.6+ use this today; the saved flag remains generic for future providers.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-checkbox v-model="programmaticToolCalling" label="Programmatic Tool Calling" hide-details
              class="display-inline" :disabled="isSystemModel" />
          </v-list-item>
          <!--
            These models reject temperature / top_p (rosetta strips both before the request reaches
            Anthropic), so the two controls are hidden via hideSamplingParams and the values are
            also dropped from the saved config — see `samplingParamsSupported` /
            `effectiveModelOptions`.
          -->
          <v-list-item v-if="showAdvanced && !samplingParamsSupported" class="ma-0 pa-0" density="compact">
            <v-alert type="warning" density="compact" variant="tonal" class="mb-2">
              {{ modelName }} does not accept temperature or top-p. Those two parameters are ignored by the provider
              and are not saved with this model configuration.
            </v-alert>
          </v-list-item>
          <ModelOptionsChoices
            v-if="showAdvanced"
            @update="updateOptions"
            :modelOptionsInput="modelOptions"
            :modelName="modelName"
            :hideSamplingParams="!samplingParamsSupported"
          />
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'"
            v-if="response !== '' && showAdvanced" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="After saving, the model will be prompted for an introduction, here is the response.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-expansion-panels>
              <v-expansion-panel title="Model's introduction" density="compact">
                <v-expansion-panel-text>
                  <!-- <p v-if="responseThinking" class="text-caption mb-2">Reasoning: {{ responseThinking }}</p> -->
                  <p class="text-body-2">{{ response }}</p>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-list-item>
        </v-list>
      </v-expand-transition>
      <v-card-actions v-if="show" density="compact">
        <Button color="primary" class="mr-4" :disabled="saveDisabled" :click="updateModel"
          icon="mdi-check-bold" size="x-large" tooltip="Save Model" density="compact" />
        <Button color="primary" class="mr-4" :disabled="isSystemModel" :click="copyModel"
          icon="mdi-content-copy" size="x-large" tooltip="Copy Model" density="compact" />
        <Button color="primary" class="mr-4" :disabled="isSystemModel" :click="initiateDeleteModel"
          icon="mdi-trash-can-outline" size="x-large" tooltip="Delete Model" density="compact" />
        <v-spacer />
        <IconToggle :value="showAdvanced" @value="toggleAdvanced" tooltip="Toggle advanced options" highlightColor="primary" />
        <v-btn v-if="!isOverlay" color="primary" class="mx-auto" icon="mdi-chevron-up"
          @click="show = false" density="compact"></v-btn>
      </v-card-actions>
    </v-form>
    <div v-if="isSystemModel" class="system-model-overlay">
      <h2 class="mr-4">System model</h2>
      <v-tooltip max-width="300"
        text="This model is provided by Mission Squad for your convenience and cannot be changed or removed."
        location="top">
        <template v-slot:activator="{ props }">
          <v-icon v-bind="props" size="medium">mdi-information</v-icon>
        </template>
      </v-tooltip>
    </div>
    <div v-if="waiting" class="card-action-overlay">
      <h2>{{ actionMessage }}</h2>
    </div>
    <ConfirmationDialog v-model="showDeleteConfirmationDialog" title="Confirm Model Deletion"
      message="Are you sure you want to delete this model configuration?" :itemName="modelNameToDelete" itemType="model"
      :loading="waiting" @confirm="confirmModelDelete" @cancel="cancelDeleteModel" />
  </v-card>
</template>
<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import ModelOptionsChoices from '@/components/sub/ModelOptions.vue'
import IconToggle from '@/components/sub/IconToggle.vue'
import Button from '@/components/sub/Button.vue'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue' // Import ConfirmationDialog
import { isSystemModelConfig, MergedModelMap, RosettaModel } from '@/types/msq'
import { ProviderKey, ProviderConfig } from '@/types/providers'
import { ModelDefaults } from '@/store/models'
import { extractThinkAndText, modelNameToId } from '@/utils/general'
import { defaultsToPTC, isRetiredModelId, supportsSamplingParams } from '@/utils/models'
import { ModelConfig, ModelOptions, SystemModelConfig, UserModelConfig } from '@/types/models'
import { formatModelPricingAmount, getModelPricingDisplayRates, getModelPricingRecord } from '@/utils/modelPricing'
import type { ModelPricingLookupResult, ModelPricingRecord } from '@/types/modelPricing'
import { ProviderIcon, ThemeName, getProviderBrandColor, getProviderIcon } from '@/utils/providerIcons'

@Options({
  props: {
    modelId: String, // This is the internal ID (UUID)
    isOverlay: Boolean,
    startExpanded: Boolean
  },
  components: {
    ModelOptionsChoices,
    IconToggle,
    Button,
    ConfirmationDialog // Register ConfirmationDialog
  },
  computed: {
    ...mapGetters(['modelConfigs', 'modelConfigById', 'modelDefaults', 'showGlobalTooltips', 'providerConfigs', 'providerKeys', 'allProviderModels', 'builtInProviders', 'modelPricingByProviderModel', 'modelPricingLoading', 'currentTheme'])
  },
  emits: ['remove-card', 'saved'],
  watch: {
    providerKey() {
      ;(this as ModelCard).applyPtcDefault()
    },
    modelName() {
      ;(this as ModelCard).applyPtcDefault()
    }
  }
})
export default class ModelCard extends Vue {
  private show_: boolean = true
  private name_: string = ''
  private description_: string = ''
  private providerKey_: ProviderKey | null = null
  private modelName_: string = '' 
  private programmaticToolCalling_: boolean = false
  /**
   * True once the user has toggled the PTC checkbox in this dialog session. The checkbox v-model is
   * the ONLY writer of the `programmaticToolCalling` setter, so the setter is a reliable
   * "user touched it" signal; every programmatic write goes to `programmaticToolCalling_` directly.
   */
  private ptcTouched_: boolean = false
  private modelOptions_: ModelOptions = {}
  private responseThinking_: string = ''
  private response_: string = ''
  private timestamp_: number = 0
  private id_: string = '' 
  private isCopy_: boolean = false 
  
  public modelConfigs!: MergedModelMap 
  public modelConfigById!: (id: string) => SystemModelConfig | UserModelConfig | undefined 
  public modelDefaults!: ModelDefaults
  public showGlobalTooltips!: boolean
  public providerConfigs!: Record<string, ProviderConfig>
  public providerKeys!: string[] 
  public builtInProviders!: string[]
  public allProviderModels!: Record<string, RosettaModel[]>
  public modelPricingByProviderModel!: (providerKey: string, model: string) => ModelPricingLookupResult | undefined
  public modelPricingLoading!: (providerKey: string, model: string) => boolean
  public currentTheme!: string
  
  public showAdvanced = false
  public loading = false
  public saveDisabled = true
  public syncDisabled = false
  public waiting = false
  public actionMessage = ''
  public modelsLoading = false
  public modelId!: string 
  public isOverlay!: boolean
  public startExpanded!: boolean

  // For delete confirmation
  public showDeleteConfirmationDialog = false
  public modelNameToDelete: string | null = null
  public iconLoadFailed = false

  get name(): string {
    return this.name_
  }
  
  set name(value: string) {
    this.name_ = value
    this.checkHash()
  }
  
  get description(): string {
    return this.description_
  }
  
  set description(value: string) {
    this.description_ = value
    this.checkHash()
  }
  
  get providerKey(): ProviderKey | null {
    return this.providerKey_
  }
  
  set providerKey(value: ProviderKey | null) {
    this.providerKey_ = value
    this.checkHash()
  }
  
  get modelName(): string {
    return this.modelName_
  }
  
  set modelName(value: string) {
    this.modelName_ = value
    this.checkHash()
  }
  
  get modelOptions(): ModelOptions {
    return this.modelOptions_
  }
  
  set modelOptions(value: ModelOptions) {
    this.modelOptions_ = value
    this.checkHash()
  }

  get programmaticToolCalling(): boolean {
    return this.programmaticToolCalling_
  }

  set programmaticToolCalling(value: boolean) {
    // Only the checkbox writes through this setter — record the manual choice so the auto
    // pre-check (`applyPtcDefault`) can never overwrite it for the rest of this dialog session.
    this.ptcTouched_ = true
    this.programmaticToolCalling_ = value
    this.checkHash()
  }
  
  get response(): string {
    return this.response_
  }
  
  set response(value: string) {
    this.response_ = value
  }
  
  get responseThinking(): string {
    return this.responseThinking_
  }
  
  set responseThinking(value: string) {
    this.responseThinking_ = value
  }
  
  get id(): string {
    return this.id_
  }
  
  set id(value: string) {
    this.id_ = value
  }
  
  get isCopy(): boolean {
    return this.isCopy_
  }
  
  set isCopy(value: boolean) {
    this.isCopy_ = value
  }
  
  get modelNameRules() {
    return [
      (v: string) => !!v || 'Model Name is required'
    ]
  }
  
  get models(): RosettaModel[] {
    if (!this.providerKey) return []
    return (this.allProviderModels[this.providerKey] || []).filter(model => !model.id.includes('embed'))
  }
  
  get mergedModelConfig(): SystemModelConfig | UserModelConfig | undefined {
    return this.modelId ? this.modelConfigById(this.modelId) : undefined
  }
  
  get existingConfig(): ModelConfig | null {
    return this.mergedModelConfig?.config || null
  }
  
  get isSystemModel(): boolean {
    return this.mergedModelConfig ? isSystemModelConfig(this.mergedModelConfig) : false
  }

  /**
   * True while this card is the "new model" dialog, i.e. there is no stored config backing it.
   * Edit and copy flows both resolve a stored config (a staged copy is written to the store by
   * `addModelCopy` before its card mounts) and therefore load their own `programmaticToolCalling`.
   */
  get isNewModelDialog(): boolean {
    return this.existingConfig == null
  }

  /** False for provider+model pairs that reject `temperature` / `top_p`. */
  get samplingParamsSupported(): boolean {
    if (!this.providerKey || !this.modelName) return true
    return supportsSamplingParams(this.providerKey, this.modelName)
  }

  /**
   * Model options actually persisted. `temperature` / `topP` are stripped for models that reject
   * sampling params so a config edited on such a model does not carry values the provider ignores.
   */
  get effectiveModelOptions(): ModelOptions {
    const options = this.modelOptions || {}
    if (this.samplingParamsSupported) {
      return options
    }
    const { temperature, topP, ...rest } = options
    return rest
  }

  /**
   * True when `model` is absent from the provider's discovered model list.
   *
   * False-positive guard: an empty or missing list means "not fetched yet" — `setModelsList` also
   * commits `[]` when a provider's model fetch fails (`src/store/providers.ts`) — so it must never
   * be read as "every model was retired".
   */
  private isModelRetired(providerKey: ProviderKey | null | undefined, model: string | null | undefined): boolean {
    if (!providerKey) return false
    const discovered = this.allProviderModels ? this.allProviderModels[providerKey] : undefined
    return isRetiredModelId(discovered, model)
  }

  /** Drives the "retired model" chip on the card header — based on the SAVED model id. */
  get configuredModelRetired(): boolean {
    const config = this.existingConfig
    if (config == null) return false
    return this.isModelRetired(config.providerKey, config.model)
  }

  /** Drives the in-dialog warning — clears as soon as the user re-selects a current model. */
  get selectedModelRetired(): boolean {
    return this.isModelRetired(this.providerKey, this.modelName)
  }

  /**
   * Pre-checks the PTC checkbox for qualifying Anthropic models in the new-model dialog, mirroring
   * the server-side default (`defaultsToPTC` in `src/utils/models.ts`) that never fires for
   * dashboard-created configs because this component always sends an explicit boolean.
   *
   * Writes the backing field directly so the manual-toggle flag stays false, and bails out once the
   * user has toggled the checkbox or when a stored config supplied the value (edit / copy).
   */
  public applyPtcDefault(): void {
    if (!this.isNewModelDialog || this.ptcTouched_ || this.isSystemModel) return
    if (!this.providerKey || !this.modelName) return
    this.programmaticToolCalling_ = defaultsToPTC(this.providerKey, this.modelName)
  }

  created() {
    this.initializeData()
    
    if (!this.providerKeys || this.providerKeys.length === 0) {
      this.$store.dispatch('fetchProviders')
    }
  }
  
  initializeData() {
    this.modelOptions_ = { ...this.modelDefaults }
    // Every (re)initialisation starts a fresh dialog session: no manual PTC choice carried over.
    this.ptcTouched_ = false

    if (this.modelId) {
      const config = this.mergedModelConfig
      
      if (config) {
        this.id_ = this.modelId
        this.isCopy_ = false
        this.show_ = !!this.startExpanded
        this.syncDisabled = this.isSystemModel
        this.saveDisabled = this.isSystemModel
        
        const { name, description, model, options, response, providerKey, timestamp, programmaticToolCalling } = config.config
        
        this.name_ = name
        this.modelName_ = model || ''
        this.description_ = description?.trim() === '' ? name : description
        this.modelOptions_ = options || { ...this.modelDefaults }
        this.programmaticToolCalling_ = programmaticToolCalling === true
        this.providerKey_ = providerKey
        this.timestamp_ = timestamp
        this.requestPricingIfNeeded()
        
        if (response) {
          const { thinking, text } = extractThinkAndText(response)
          this.response_ = text
          this.responseThinking_ = thinking
        }
      } else {
        console.log(`ModelCard: ID ${this.modelId} not found in store, assuming new copy.`)
        this.id_ = this.modelId
        this.isCopy_ = true
        this.show_ = true 
        this.saveDisabled = true
      }
    } else {
      console.log("ModelCard: No modelId provided, creating new card instance.")
      this.isCopy_ = true
      this.show_ = true 
      this.saveDisabled = true
    }
  }

  get providerInitials(): string {
    const raw = String(this.providerKey || '').trim()
    if (!raw) return 'AI'
    const cleaned = raw.replace(/[^a-zA-Z0-9]/g, '')
    return cleaned.slice(0, 2).toUpperCase() || 'AI'
  }

  get brandColor(): string {
    return getProviderBrandColor(this.providerKey || '')
  }

  get icon(): ProviderIcon | null {
    return this.providerKey ? getProviderIcon(this.providerKey, this.currentTheme as ThemeName) : null
  }

  get iconMode(): ProviderIcon['mode'] | null {
    return this.icon?.mode || null
  }

  get iconMaskStyle(): Record<string, string> {
    const icon = this.icon
    if (!icon) return {}
    const url = `url("${icon.src}")`
    return {
      '-webkit-mask-image': url,
      'mask-image': url,
      backgroundColor: icon.tint || 'currentColor'
    }
  }

  get showStandaloneIcon(): boolean {
    return !!this.icon && this.iconLoadFailed !== true
  }

  get pricingLookup(): ModelPricingLookupResult | undefined {
    if (!this.providerKey || !this.modelName) return undefined
    return this.modelPricingByProviderModel(this.providerKey, this.modelName)
  }

  get pricingRecord(): ModelPricingRecord | null {
    return getModelPricingRecord(this.pricingLookup)
  }

  get pricingLoading(): boolean {
    if (!this.providerKey || !this.modelName) return false
    return this.modelPricingLoading(this.providerKey, this.modelName)
  }

  get inputPricingLabel(): string {
    const { input } = getModelPricingDisplayRates(this.pricingRecord)
    return formatModelPricingAmount(input)
  }

  get outputPricingLabel(): string {
    const { output } = getModelPricingDisplayRates(this.pricingRecord)
    return formatModelPricingAmount(output)
  }

  get pricingTierLabel(): string {
    const { input, output } = getModelPricingDisplayRates(this.pricingRecord)
    const tier = input?.tier || output?.tier
    return tier && tier !== 'standard' ? tier : ''
  }

  modelNameExists(name: string): boolean {
    if (!this.modelConfigs) {
      return false
    }
    const existing = this.modelConfigs[name]
    return existing != null && (this.isCopy || existing.config.id !== this.id)
  }

  get config(): Omit<ModelConfig, 'id' | 'timestamp' | 'url' | 'apiKey' | 'response' | 'hash'> | undefined {
    if (!this.name || !this.providerKey || !this.modelName) {
      return undefined 
    }

    return {
      name: this.name,
      model: this.modelName, 
      description: (this.description?.trim() === '') ? this.name : this.description,
      providerKey: this.providerKey,
      programmaticToolCalling: this.programmaticToolCalling,
      options: this.effectiveModelOptions,
    }
  }

  get show(): boolean {
    return this.show_
  }

  set show(value: boolean) {
    this.show_ = value
  }

  toggleAdvanced() {
    this.showAdvanced = !this.showAdvanced
  }

  modelSelected(selectedModelName: string) {
    this.modelName = selectedModelName 
    if (this.isCopy && this.name.trim() === '' && selectedModelName) {
      this.name = selectedModelName
    }
    void this.requestPricingIfNeeded()
  }

  providerChanged(selectedProviderKey: ProviderKey | null) {
    this.providerKey = selectedProviderKey
    this.modelName = ''
  }

  async requestPricingIfNeeded() {
    if (!this.providerKey || !this.modelName) return
    await this.$store.dispatch('fetchModelPricing', {
      items: [{ providerKey: this.providerKey, model: this.modelName }]
    })
  }

  onIconError(): void {
    this.iconLoadFailed = true
  }

  async copyModel() {
    if (this.isSystemModel) return 

    const currentConfig = this.existingConfig ?? this.config 
    if (!currentConfig) return

    console.log('Copying model with current config:', JSON.stringify(currentConfig))

    let copyNum = 1
    let newName = `Copy of ${this.name}`
    while (this.modelNameExists(newName)) {
      copyNum++
      newName = `Copy of ${this.name} (${copyNum})`
    }
    const newId = modelNameToId(newName) 

    console.log('New model name:', newName, 'New ID:', newId)

    const newConfigData: ModelConfig = {
      id: newId, 
      timestamp: Date.now(), 
      name: newName, 
      model: this.modelName, 
      description: this.description, 
      providerKey: this.providerKey!, 
      programmaticToolCalling: this.programmaticToolCalling,
      response: '',
      options: this.modelOptions ? { ...this.effectiveModelOptions } : null
    }

    console.log('Created new config object for copy:', JSON.stringify(newConfigData))

    this.waiting = true
    this.actionMessage = 'Creating copy...'
    await this.$store.dispatch('addModelCopy', newConfigData)
    this.waiting = false
    this.actionMessage = ''
  }

  checkHash() {
    const requiredFilled = !!this.name && !!this.providerKey && !!this.modelName
    this.saveDisabled = this.isSystemModel || !requiredFilled
  }

  async syncModels() {
    if (!this.providerKey) {
      this.$store.dispatch('updateSnackbar', { text: 'Please select a provider first.', timeout: 3000 })
      return
    }
    this.modelsLoading = true
    this.syncDisabled = true
    await this.$store.dispatch('fetchProviderModels', { providerConfig: { providerKey: this.providerKey } })
    this.modelsLoading = false
    this.syncDisabled = this.isSystemModel 
  }

  updateOptions(options: ModelOptions) {
    this.modelOptions = options
  }

  autofillDescription() {
    this.$store.dispatch('updateSnackbar', { text: 'Autofill description not implemented yet.', timeout: 3000 })
  }

  autofillName() {
    if (this.modelName) {
      this.name = this.modelName
    }
  }

  async updateModel() {
    const configData = this.config 
    if (configData && !this.saveDisabled) {
      this.waiting = true
      this.actionMessage = 'Saving...'

      const success = await this.$store.dispatch('setModelConfig', configData)

      if (success) {
        this.isCopy = false
        // A successful save ends the dialog session. The create overlay keeps this component
        // mounted between openings (v-dialog content is not destroyed), so clear the manual-toggle
        // flag here as well as in initializeData to keep the next session's pre-check live.
        this.ptcTouched_ = false
        this.$emit('saved', { name: this.name })
      }

      this.waiting = false
      this.actionMessage = ''
    } else {
      this.$store.dispatch('updateSnackbar', { text: 'Cannot save: Missing required fields or no changes detected.', timeout: 3000 })
    }
  }

  initiateDeleteModel() {
    if (this.isSystemModel) {
      return
    }
    if (this.isCopy) {
      this.$emit('remove-card')
      return
    }
    if (this.id) {
      this.modelNameToDelete = this.name
      this.showDeleteConfirmationDialog = true
    }
  }

  async confirmModelDelete() {
    if (this.modelNameToDelete && this.id) {
      this.waiting = true
      this.actionMessage = 'Deleting...'
      const success = await this.$store.dispatch('deleteModel', this.modelNameToDelete) 
      this.waiting = false
      this.actionMessage = ''
      if (success) {
        this.$emit('remove-card')
      }
    }
    this.cancelDeleteModel()
  }

  cancelDeleteModel() {
    this.showDeleteConfirmationDialog = false
    this.modelNameToDelete = null
  }
}
</script>
<style scoped lang="scss">
.card {
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.model-summary {
  cursor: pointer;
}
.model-summary-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-on-surface));
}
.model-summary-avatar-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.model-summary-avatar-mask {
  display: block;
  width: 100%;
  height: 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}
.model-summary-provider {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
}
.model-summary-pricing {
  padding-left: 72px;
}
.model-summary-expand {
  flex-shrink: 0;
  margin-left: 8px;
}
.min-width-0 {
  min-width: 0;
}
.system-model-overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0; 
  bottom: 40px; 
  background-color: rgba(var(--v-theme-surface-variant), 0.8); 
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10; 
  border-radius: inherit; 
  padding: 10px;
  box-sizing: border-box;
  text-align: center;
  color: rgba(var(--v-theme-on-surface), 0.7); 
}
.card-action-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  border-radius: inherit;
}
</style>
